import { useTranslation } from 'react-i18next';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import React, { PropsWithChildren } from 'react';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { ProductInterface } from '../../../services/exports/Interfaces';
import classnames from 'classnames';
import useScreenType from '../../../hooks/utility/useScreenType';
import collect from 'collect.js';

interface Props extends PropsWithChildren {
  product: WithActualPrice<ProductInterface>;
  className?: string;
}

const Pfand = ({ product, className }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:Product:ProductItem' });
  const { formatCurrency } = useAppMethods();

  if (!product?.nutrition_info?.pfand) {
    return null;
  }

  const value = formatCurrency(product?.nutrition_info?.pfand);
  const packagingMaterialCopy = product?.nutrition_info?.packaging_material
    ? `(${product?.nutrition_info?.packaging_material})`
    : null;
  const pfandCopy = t('pfand', { value });
  const copy = [pfandCopy, packagingMaterialCopy].filter(Boolean).join(' ');

  return <p className={classnames('w-fit text-xs line-clamp-1', className)}>{copy}</p>;
};

const PriceBreakdown = ({ product, className }: Props) => {
  const { formatCurrency } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:Product:ProductItem' });
  const { isMobile } = useScreenType();

  const basePriceValue =
    product?.nutrition_info?.base_price && formatCurrency(product?.nutrition_info?.base_price.value_per_unit);
  const basePriceCopy =
    product?.nutrition_info?.base_price && `${basePriceValue}/${product?.nutrition_info?.base_price.unit}`;
  const netQuantityCopy =
    product?.nutrition_info?.net_quantity &&
    `${product?.nutrition_info?.net_quantity.value} ${product?.nutrition_info?.net_quantity.unit}`;
  const alcoCopy =
    product?.nutrition_info?.alcohol_by_volume_percent &&
    t('alcohol_by_volume_percent', { value: product?.nutrition_info?.alcohol_by_volume_percent });
  const filteredCopy = [netQuantityCopy, alcoCopy, basePriceCopy].filter(Boolean).join(', ');

  if (!filteredCopy) {
    return null;
  }

  const copy = isMobile ? filteredCopy : `(${filteredCopy})`;

  return <div className={classnames('w-fit text-xs text-black font-normal line-clamp-1', className)}>{copy}</div>;
};

const HighCaffeineWarning = ({ product }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:Product:ProductItem' });

  const shouldPresentCaffeineWarning =
    product?.nutrition_info?.caffeine_mg_per_100ml > 0 ||
    collect(product?.nutrition_info?.allergens_list ?? [])
      .map((item) => (item ?? '').toLowerCase())
      .intersect(['koffein', 'caffeine'])
      .isNotEmpty();

  if (!shouldPresentCaffeineWarning) {
    return null;
  }

  return (
    <p className="w-fit bg-[hsl(38_92%_50%/0.1)] text-xs p-1 rounded flex items-center justify-center text-[hsl(38_92%_50%)] font-medium">
      {t('high_caffeine_warning_required')}
    </p>
  );
};

export default { Pfand, PriceBreakdown, HighCaffeineWarning };
